import { doc, getDoc, updateDoc, increment, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';

// Generate a unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitorId');
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitorId', visitorId);
  }
  return visitorId;
};

// Check if this visitor has viewed this post in this session
const hasViewedInSession = (postId) => {
  const sessionViews = sessionStorage.getItem('viewedPosts');
  if (!sessionViews) return false;
  const viewedPosts = JSON.parse(sessionViews);
  return viewedPosts.includes(postId);
};

// Mark post as viewed in this session
const markViewedInSession = (postId) => {
  const sessionViews = sessionStorage.getItem('viewedPosts');
  const viewedPosts = sessionViews ? JSON.parse(sessionViews) : [];
  if (!viewedPosts.includes(postId)) {
    viewedPosts.push(postId);
    sessionStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
  }
};

// Track a view for a post (unique per visitor)
export const trackView = async (postId) => {
  try {
    // Check if already viewed in this session
    if (hasViewedInSession(postId)) {
      return false; // Already counted this session
    }

    const visitorId = getVisitorId();
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);

    if (!postDoc.exists()) return false;

    const postData = postDoc.data();
    const viewedBy = postData.viewedBy || [];

    // Check if this unique visitor has already viewed this post
    if (!viewedBy.includes(visitorId)) {
      // New unique view - increment both total and unique views
      await updateDoc(postRef, {
        views: increment(1),
        viewedBy: arrayUnion(visitorId),
        lastViewedAt: new Date(),
      });
      markViewedInSession(postId);
      return true;
    } else {
      // Repeat visitor - just mark as viewed in session
      markViewedInSession(postId);
      return false;
    }
  } catch (error) {
    // Silently fail if permissions error (Firebase rules not set up yet)
    if (error.code === 'permission-denied') {
      console.warn('View tracking disabled: Firebase permissions not configured');
    } else {
      console.error('Error tracking view:', error);
    }
    return false;
  }
};

// Get view count for a post
export const getViewCount = async (postId) => {
  try {
    const postRef = doc(db, 'posts', postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) return 0;
    
    const postData = postDoc.data();
    return postData.views || 0;
  } catch (error) {
    console.error('Error getting view count:', error);
    return 0;
  }
};

// Format view count for display (e.g., 1.2K, 5.6M)
export const formatViewCount = (count) => {
  if (!count || count === 0) return '0 views';
  if (count === 1) return '1 view';
  if (count < 1000) return `${count} views`;
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K views`;
  return `${(count / 1000000).toFixed(1)}M views`;
};
